import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';

const Settings = () => {
  const { settings, updateSettings, t } = useAppContext();
  const [localSettings, setLocalSettings] = useState(settings);
  const [saveStatus, setSaveStatus] = useState(null);

  // PIN Protection State
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);

  const targetPin = settings.system?.settingsPin || '1234';

  const handleInputChange = (section, field, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const handleSave = () => {
    updateSettings(localSettings);
    setSaveStatus('success');
    setTimeout(() => setSaveStatus(null), 3000);
  };

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pinInput === targetPin) {
      setIsAuthorized(true);
      setPinError(false);
    } else {
      setPinError(true);
      setPinInput('');
    }
  };

  const Section = ({ title, children, icon }) => (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center gap-3">
        <SafeIcon name={icon} className="text-amber-600 w-5 h-5" />
        <h2 className="font-bold text-gray-800">{title}</h2>
      </div>
      <div className="p-6 space-y-4">
        {children}
      </div>
    </div>
  );

  const InputField = ({ label, value, onChange, placeholder, type = "text" }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-amber-500 transition-all text-sm"
      />
    </div>
  );

  if (!isAuthorized) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center animate-fade-in">
        <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-xl max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon name="Lock" className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('settings.locked')}</h2>
          <p className="text-gray-500 mb-8 text-sm">{t('settings.lockedDesc')}</p>
          <form onSubmit={handlePinSubmit} className="space-y-4">
            <input
              type="password"
              value={pinInput}
              onChange={(e) => { setPinError(false); setPinInput(e.target.value); }}
              placeholder="••••"
              autoFocus
              className={`w-full text-center text-3xl tracking-widest p-3 border-2 rounded-xl outline-none transition-all ${pinError ? 'border-red-500 bg-red-50' : 'border-gray-200 focus:border-amber-500'}`}
            />
            <button type="submit" className="w-full bg-amber-600 hover:bg-amber-700 text-white py-3 rounded-xl font-bold shadow-lg transition-all">
              {t('actions.unlock')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
          <p className="text-gray-500 mt-1">{t('settings.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setIsAuthorized(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 font-medium transition-all">
            {t('actions.lock')}
          </button>
          <button onClick={handleSave} className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded-lg font-bold shadow-md transition-all flex items-center gap-2">
            {saveStatus === 'success' ? <SafeIcon name="Check" /> : <SafeIcon name="Save" />}
            {saveStatus === 'success' ? t('actions.saved') : t('actions.save')}
          </button>
        </div>
      </div>

      <Section title={t('settings.companyIdentity')} icon="Briefcase">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField label="Bakery Name" value={localSettings.company.name} onChange={(val) => handleInputChange('company', 'name', val)} />
          <InputField label="Logo URL" value={localSettings.company.logo} onChange={(val) => handleInputChange('company', 'logo', val)} />
          <InputField label="Street Address" value={localSettings.company.address} onChange={(val) => handleInputChange('company', 'address', val)} />
          <InputField label="Postcode & City" value={localSettings.company.location} onChange={(val) => handleInputChange('company', 'location', val)} />
          <InputField label="Phone" value={localSettings.company.phone} onChange={(val) => handleInputChange('company', 'phone', val)} />
          <InputField label="Email" value={localSettings.company.email} onChange={(val) => handleInputChange('company', 'email', val)} />
          <InputField label="Website" value={localSettings.company.website} onChange={(val) => handleInputChange('company', 'website', val)} />
          <InputField label="Contact Person" value={localSettings.company.contactPerson} onChange={(val) => handleInputChange('company', 'contactPerson', val)} />
        </div>
      </Section>

      <Section title={t('settings.printPrefs')} icon="Printer">
        <div className="space-y-4">
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={localSettings.print.showLogo} onChange={(e) => handleInputChange('print', 'showLogo', e.target.checked)} className="w-4 h-4 text-amber-600 rounded" />
              {t('settings.showLogo')}
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={localSettings.print.showAddress} onChange={(e) => handleInputChange('print', 'showAddress', e.target.checked)} className="w-4 h-4 text-amber-600 rounded" />
              {t('settings.showAddress')}
            </label>
          </div>
          <InputField label={t('settings.footerText')} value={localSettings.print.footerText} onChange={(val) => handleInputChange('print', 'footerText', val)} />
        </div>
      </Section>

      <Section title={t('settings.systemConfig')} icon="Cpu">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.language')}</label>
            <select
              value={localSettings.system.language}
              onChange={(e) => handleInputChange('system', 'language', e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-amber-500 transition-all text-sm"
            >
              <option value="da">Dansk (Danish)</option>
              <option value="en">English</option>
            </select>
          </div>
          <InputField label={t('settings.securityPin')} value={localSettings.system.settingsPin} onChange={(val) => handleInputChange('system', 'settingsPin', val)} />
          <InputField label={t('settings.gasUrl')} value={localSettings.system.gasUrl} onChange={(val) => handleInputChange('system', 'gasUrl', val)} />
          <InputField label={t('settings.sheetId')} value={localSettings.system.spreadsheetId} onChange={(val) => handleInputChange('system', 'spreadsheetId', val)} />
        </div>
      </Section>
    </div>
  );
};

export default Settings;